// ABOUTME: Right-anchored drawer listing the user's notifications newest-first, with an unread/all
// ABOUTME: tab filter, per-row "mark as read" and deep-link actions, relative timestamps and
// ABOUTME: explicit empty/loading states. In-app deep links navigate in place; external links and
// ABOUTME: Boomi certification/publish outcomes open a new tab.

import {
    ArrowForwardIcon,
    Avatar,
    Box,
    Button,
    CheckCircleOutlinedIcon,
    Chip,
    CloseIcon,
    Divider,
    DoneAllIcon,
    DoneIcon,
    Drawer,
    ErrorOutlineOutlinedIcon,
    IconButton,
    InfoOutlinedIcon,
    List,
    ListItem,
    NotificationsNoneOutlinedIcon,
    OpenInNewIcon,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@ds/coolshop';
// Allowlisted direct MUI import: the DS has no LinearProgress equivalent (see CLAUDE.md).
import LinearProgress from '@mui/material/LinearProgress';
import { MouseEvent, ReactNode, useEffect } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { NotificationsQuery } from '../graphql/notifications.generated';

type NotificationItem = NotificationsQuery['notifications'][number];

// Drawer width: wide enough for a title + two-line message without wrapping every word, narrow
// enough to leave the page visible behind it. Capped to the viewport on small screens. Passed to
// the DS Drawer itself, which otherwise sizes its paper at 25% of the viewport.
const PANEL_WIDTH = 'min(420px, 100vw)';

// Server cap of the "All" view (SHOW_ALL_LIMIT in getNotifications.ts): when the list reaches it
// the panel tells the user older notifications are not shown.
const SHOW_ALL_LIMIT = 100;

// Rows rendered as placeholders while the very first fetch is in flight.
const SKELETON_ROWS = 3;

type NotificationType = 'info' | 'success' | 'error';

// Maps a notification type to its row icon and the Avatar color that carries the semantics;
// extend when new NotificationType values are added.
const TYPE_ICONS: Record<NotificationType, ReactNode> = {
    info: <InfoOutlinedIcon fontSize="small" />,
    success: <CheckCircleOutlinedIcon fontSize="small" />,
    error: <ErrorOutlineOutlinedIcon fontSize="small" />,
};

const TYPE_COLORS: Record<NotificationType, 'info' | 'success' | 'error'> = {
    info: 'info',
    success: 'success',
    error: 'error',
};

const resolveType = (type: string | null | undefined): NotificationType =>
    type === 'success' || type === 'error' ? type : 'info';

// Deep links built by the server are app routes ("/hierarchies/..."); definition-based ones can be
// any URL. Same-origin links resolve to their in-app path so they navigate without a reload.
const resolveInAppPath = (deepLink: string): string | null => {
    try {
        const url = new URL(deepLink, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
};

// Boomi certification/publish outcomes (payload.operation set by the server notifyBoomiOutcome helper) open
// their deep link in a new tab, so the page the user is working on is not replaced.
const NEW_TAB_OPERATIONS = [
    'prototype-certification',
    'model-certification',
    'service-contract-publish',
    'warranty-publish',
    'plant-publish',
];

const opensInNewTab = (notification: NotificationItem): boolean =>
    NEW_TAB_OPERATIONS.includes(notification.payload?.operation);

// "3 minutes ago" / "yesterday" for recent notifications, absolute date beyond a week.
const formatNotificationTime = (intl: IntlShape, createdAt: string): string => {
    const date = new Date(createdAt);
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const absSeconds = Math.abs(diffSeconds);
    const options = { numeric: 'auto' as const };
    if (absSeconds < 60) return intl.formatRelativeTime(diffSeconds, 'second', options);
    if (absSeconds < 3600) {
        return intl.formatRelativeTime(Math.round(diffSeconds / 60), 'minute', options);
    }
    if (absSeconds < 86400) {
        return intl.formatRelativeTime(Math.round(diffSeconds / 3600), 'hour', options);
    }
    if (absSeconds < 7 * 86400) {
        return intl.formatRelativeTime(Math.round(diffSeconds / 86400), 'day', options);
    }
    return intl.formatDate(date, { dateStyle: 'medium', timeStyle: 'short' });
};

type NotificationPanelProps = {
    open: boolean;
    onClose: () => void;
    notifications: NotificationItem[];
    unreadCount: number;
    loading: boolean;
    showAll: boolean;
    onToggleShowAll: (showAll: boolean) => void;
    onMarkRead: (id: string) => void;
    onMarkAllRead: () => void;
    onRefetch: () => void;
};

export function NotificationPanel({
    open,
    onClose,
    notifications,
    unreadCount,
    loading,
    showAll,
    onToggleShowAll,
    onMarkRead,
    onMarkAllRead,
    onRefetch,
}: NotificationPanelProps) {
    const intl = useIntl();
    const navigate = useNavigate();

    // Catch-up on open so nothing is missed while the panel was closed / the socket dropped.
    useEffect(() => {
        if (open) onRefetch();
    }, [open, onRefetch]);

    const openDeepLink = (deepLink: string, newTab: boolean) => {
        const inAppPath = resolveInAppPath(deepLink);
        if (inAppPath && !newTab) {
            navigate(inAppPath);
            onClose();
        } else {
            window.open(deepLink, '_blank', 'noopener');
        }
    };

    const handleNotificationClick = (notification: NotificationItem) => {
        if (!notification.read) {
            onMarkRead(notification.id);
        }
        if (notification.deepLink) {
            openDeepLink(notification.deepLink, opensInNewTab(notification));
        }
    };

    // Row action buttons must not trigger the row click (which would also open the deep link).
    const handleMarkReadClick = (event: MouseEvent<HTMLButtonElement>, id: string) => {
        event.stopPropagation();
        onMarkRead(id);
    };

    const isInitialLoad = loading && notifications.length === 0;
    const isEmpty = !loading && notifications.length === 0;
    const isCapped = showAll && notifications.length >= SHOW_ALL_LIMIT;

    const markAllReadLabel = intl.formatMessage({
        id: 'notifications.markAllRead',
        defaultMessage: 'Mark all as read',
    });
    const markReadLabel = intl.formatMessage({
        id: 'notifications.markRead',
        defaultMessage: 'Mark as read',
    });
    const openLabel = intl.formatMessage({
        id: 'notifications.open',
        defaultMessage: 'Open',
    });
    const closeLabel = intl.formatMessage({
        id: 'notifications.close',
        defaultMessage: 'Close',
    });

    return (
        <Drawer anchor="right" width={PANEL_WIDTH} open={open} onClose={onClose}>
            <Stack width="100%" height="100%">
                <Stack direction="row" alignItems="center" gap={1} px={2} pt={2} pb={1}>
                    <Typography variant="h5" color="primary">
                        <FormattedMessage id="notifications.title" defaultMessage="Notifications" />
                    </Typography>
                    {unreadCount > 0 && (
                        <Chip size="small" color="primary" label={String(unreadCount)} />
                    )}
                    <Box flexGrow={1} />
                    <Tooltip title={markAllReadLabel} placement="bottom">
                        {/* The span keeps the tooltip reachable while the button is disabled. */}
                        <span>
                            <IconButton
                                size="small"
                                aria-label={markAllReadLabel}
                                onClick={onMarkAllRead}
                                disabled={unreadCount === 0}
                            >
                                <DoneAllIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" aria-label={closeLabel} onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Box px={2}>
                    <Tabs
                        value={showAll ? 'all' : 'unread'}
                        onChange={(_, value) => onToggleShowAll(value === 'all')}
                        variant="standard"
                        orientation="horizontal"
                    >
                        <Tab
                            value="unread"
                            label={intl.formatMessage(
                                {
                                    id: 'notifications.tab.unread',
                                    defaultMessage: 'Unread ({count})',
                                },
                                { count: unreadCount },
                            )}
                        />
                        <Tab
                            value="all"
                            label={intl.formatMessage({
                                id: 'notifications.tab.all',
                                defaultMessage: 'All',
                            })}
                        />
                    </Tabs>
                </Box>
                <Divider />
                {/* Thin progress bar while a (re)fetch is in flight with content already visible. */}
                <Box minHeight={4}>{loading && <LinearProgress />}</Box>
                <Box flexGrow={1} overflow="auto">
                    {isInitialLoad && (
                        <Stack gap={2} p={2}>
                            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
                                <Stack key={index} direction="row" gap={2} alignItems="center">
                                    <Skeleton variant="circular" width={32} height={32} />
                                    <Stack flex={1} gap={0.5}>
                                        <Skeleton variant="text" width="60%" />
                                        <Skeleton variant="text" width="90%" />
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                    {isEmpty && (
                        <Stack alignItems="center" gap={1} px={3} py={6} textAlign="center">
                            <NotificationsNoneOutlinedIcon fontSize="large" color="disabled" />
                            <Typography variant="body1" color="textSecondary">
                                {showAll ? (
                                    <FormattedMessage
                                        id="notifications.empty"
                                        defaultMessage="You have no notifications."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="notifications.emptyUnread"
                                        defaultMessage="You're all caught up - No unread notifications."
                                    />
                                )}
                            </Typography>
                            {!showAll && (
                                <Button variant="text" onClick={() => onToggleShowAll(true)}>
                                    {intl.formatMessage({
                                        id: 'notifications.viewAll',
                                        defaultMessage: 'View all notifications',
                                    })}
                                </Button>
                            )}
                        </Stack>
                    )}
                    {notifications.length > 0 && (
                        <List fullwidth>
                            {notifications.map(notification => {
                                const type = resolveType(notification.type);
                                const inAppPath =
                                    notification.deepLink && !opensInNewTab(notification)
                                        ? resolveInAppPath(notification.deepLink)
                                        : null;
                                return (
                                    <ListItem
                                        key={notification.id}
                                        divider
                                        cursor={
                                            notification.deepLink || !notification.read
                                                ? 'pointer'
                                                : 'default'
                                        }
                                        fullWidth
                                        onClick={() => handleNotificationClick(notification)}
                                        startAdornment={
                                            <Avatar
                                                color={
                                                    notification.read ? 'grey' : TYPE_COLORS[type]
                                                }
                                                size="sm"
                                            >
                                                {TYPE_ICONS[type]}
                                            </Avatar>
                                        }
                                        endAdornment={
                                            <Stack direction="row" alignItems="center">
                                                {!notification.read && (
                                                    <Tooltip title={markReadLabel} placement="left">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={markReadLabel}
                                                            onClick={event =>
                                                                handleMarkReadClick(
                                                                    event,
                                                                    notification.id,
                                                                )
                                                            }
                                                        >
                                                            <DoneIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {notification.deepLink && (
                                                    <Tooltip title={openLabel} placement="left">
                                                        {inAppPath ? (
                                                            <ArrowForwardIcon
                                                                color="action"
                                                                fontSize="small"
                                                            />
                                                        ) : (
                                                            <OpenInNewIcon
                                                                color="action"
                                                                fontSize="small"
                                                            />
                                                        )}
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        }
                                    >
                                        {/* The DS ListItem body is nowrap/ellipsis; restore
                                            wrapping so titles and messages can span lines. */}
                                        <Box
                                            minWidth={0}
                                            flexGrow={1}
                                            whiteSpace="normal"
                                            opacity={notification.read ? 0.7 : 1}
                                            px={1}
                                        >
                                            <Typography variant="body1" wordBreak="break-word">
                                                {notification.read ? (
                                                    notification.title
                                                ) : (
                                                    <strong>{notification.title}</strong>
                                                )}
                                            </Typography>
                                            {notification.message && (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    wordBreak="break-word"
                                                >
                                                    {notification.message}
                                                </Typography>
                                            )}
                                            <Tooltip
                                                title={intl.formatDate(
                                                    new Date(notification.createdAt),
                                                    { dateStyle: 'full', timeStyle: 'short' },
                                                )}
                                                placement="bottom"
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    component="span"
                                                >
                                                    {formatNotificationTime(
                                                        intl,
                                                        notification.createdAt,
                                                    )}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                    {isCapped && (
                        <Box px={2} py={1.5} textAlign="center">
                            <Typography variant="caption" color="textSecondary">
                                {intl.formatMessage(
                                    {
                                        id: 'notifications.showAllCapped',
                                        defaultMessage: 'Showing the latest {limit} notifications.',
                                    },
                                    { limit: SHOW_ALL_LIMIT },
                                )}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Stack>
        </Drawer>
    );
}
